import Decimal, { type Numeric } from "decimal.js-light";
import type { LineItemType } from "./status.js";

/**
 * Money engine (spec §12). All arithmetic uses fixed-precision decimals.
 *
 * Documented calculation order — covered by automated tests:
 *  1. Line gross = quantity × unit price (excl. tax).
 *  2. Line discount: percentage first, then fixed amount, floored at zero.
 *     Line net is rounded to 0.01 (commercial rounding, half-up).
 *  3. Subtotal = Σ line nets.
 *  4. Document-level discount (percent or fixed) is distributed across
 *     taxable lines proportional to their net; per-line shares are rounded
 *     to 0.01 and the residual is booked on the largest line so the sum
 *     always matches exactly.
 *  5. Tax is computed per rate group on the grouped discounted net,
 *     rounded to 0.01 per group (Swiss tax-summary practice).
 *  6. Total incl. tax = net + Σ group taxes.
 *  7. Grand total = total rounded to the configured rounding step
 *     (0.01 default, 0.05 Swiss cash rounding optional). The rounding
 *     adjustment is reported separately.
 */

export const ROUND_HALF_UP = Decimal.ROUND_HALF_UP;
export const CENT = new Decimal("0.01");

export type RoundingStep = "0.01" | "0.05";

/** Round a monetary amount to 0.01, commercial rounding (half away from zero). */
export function roundCent(value: Numeric): Decimal {
  return new Decimal(value).toDecimalPlaces(2, ROUND_HALF_UP);
}

/** Round to a configurable step ("0.01" or "0.05"), commercial rounding. */
export function roundToStep(value: Numeric, step: RoundingStep): Decimal {
  const s = new Decimal(step);
  return new Decimal(value)
    .div(s)
    .toDecimalPlaces(0, ROUND_HALF_UP)
    .times(s)
    .toDecimalPlaces(2);
}

export interface CalcLineInput {
  type: LineItemType;
  quantity: Numeric;
  unitPrice: Numeric;
  discountPercent?: Numeric | null;
  discountAmount?: Numeric | null;
  /** Tax rate in percent, e.g. 8.1. */
  taxRate: Numeric;
}

export interface CalculatedLine {
  /** Net excl. tax after line discounts, rounded to 0.01. Zero for text lines. */
  net: Decimal;
}

export interface TaxBreakdownEntry {
  rate: Decimal;
  net: Decimal;
  tax: Decimal;
}

export interface DocumentTotals {
  /** Net per input line, same order. */
  lines: CalculatedLine[];
  /** Σ line nets before document discount. */
  subtotal: Decimal;
  /** Document discount amount actually applied. */
  documentDiscount: Decimal;
  /** Subtotal − documentDiscount. */
  netExclTax: Decimal;
  taxBreakdown: TaxBreakdownEntry[];
  taxTotal: Decimal;
  totalInclTax: Decimal;
  roundingStep: RoundingStep;
  roundingAdjustment: Decimal;
  grandTotal: Decimal;
}

export interface CalcDocumentOptions {
  documentDiscountPercent?: Numeric | null;
  documentDiscountAmount?: Numeric | null;
  roundingStep?: RoundingStep;
}

const ZERO = new Decimal(0);

/** Steps 1–2 for a single line. Text lines always net to zero. */
export function calcLineNet(line: CalcLineInput): Decimal {
  if (line.type === "text") return ZERO;
  const gross = new Decimal(line.quantity).times(new Decimal(line.unitPrice));
  let net = gross;
  const pct = new Decimal(line.discountPercent ?? 0);
  if (pct.gt(0)) net = net.times(new Decimal(1).minus(pct.div(100)));
  const amt = new Decimal(line.discountAmount ?? 0);
  if (amt.gt(0)) net = net.minus(amt);
  // Discounts may never push a non-negative line below zero; intentionally
  // negative lines (type "discount") keep their sign.
  if (net.isNegative() && !gross.isNegative()) return ZERO;
  return roundCent(net);
}

export function calcDocumentTotals(
  inputLines: readonly CalcLineInput[],
  options: CalcDocumentOptions = {},
): DocumentTotals {
  const step = options.roundingStep ?? "0.01";
  const lines = inputLines.map((l) => ({ net: calcLineNet(l) }));
  const subtotal = lines.reduce((acc, l) => acc.plus(l.net), ZERO);

  // Step 4: document discount.
  let target = ZERO;
  const docPct = new Decimal(options.documentDiscountPercent ?? 0);
  const docAmt = new Decimal(options.documentDiscountAmount ?? 0);
  if (docPct.gt(0)) target = roundCent(subtotal.times(docPct.div(100)));
  if (docAmt.gt(0)) target = target.plus(docAmt);
  if (target.gt(subtotal)) target = subtotal;
  if (target.lt(0)) target = ZERO;

  // Distribute proportionally across non-zero lines; residual on largest line.
  const discountedNets: Decimal[] = lines.map((l) => l.net);
  if (target.gt(0) && subtotal.gt(0)) {
    let distributed = ZERO;
    let largestIdx = -1;
    let largestNet = ZERO;
    lines.forEach((l, i) => {
      if (l.net.gt(largestNet)) {
        largestNet = l.net;
        largestIdx = i;
      }
      if (l.net.isZero()) return;
      const share = roundCent(target.times(l.net).div(subtotal));
      discountedNets[i] = l.net.minus(share);
      distributed = distributed.plus(share);
    });
    const residual = target.minus(distributed);
    if (!residual.isZero() && largestIdx >= 0) {
      discountedNets[largestIdx] = discountedNets[largestIdx]!.minus(residual);
    }
  }

  const netExclTax = discountedNets.reduce((acc, n) => acc.plus(n), ZERO);

  // Step 5: group by tax rate.
  const groups = new Map<string, { rate: Decimal; net: Decimal }>();
  inputLines.forEach((l, i) => {
    const rate = new Decimal(l.taxRate);
    const net = discountedNets[i] ?? ZERO;
    if (net.isZero()) return;
    const key = rate.toString();
    const g = groups.get(key);
    if (g) g.net = g.net.plus(net);
    else groups.set(key, { rate, net });
  });
  const taxBreakdown: TaxBreakdownEntry[] = [...groups.values()]
    .map((g) => ({
      rate: g.rate,
      net: roundCent(g.net),
      tax: roundCent(g.net.times(g.rate).div(100)),
    }))
    .sort((a, b) => b.rate.comparedTo(a.rate));
  const taxTotal = taxBreakdown.reduce((acc, t) => acc.plus(t.tax), ZERO);

  const totalInclTax = netExclTax.plus(taxTotal);
  const grandTotal = roundToStep(totalInclTax, step);

  return {
    lines,
    subtotal,
    documentDiscount: target,
    netExclTax,
    taxBreakdown,
    taxTotal,
    totalInclTax,
    roundingStep: step,
    roundingAdjustment: grandTotal.minus(totalInclTax),
    grandTotal,
  };
}

/** Format a decimal amount for display, e.g. 1'234.50 (Swiss apostrophe grouping). */
export function formatMoney(value: Numeric, currency = "CHF"): string {
  const fixed = roundCent(value).toFixed(2);
  const negative = fixed.startsWith("-");
  const abs = negative ? fixed.slice(1) : fixed;
  const [intPart = "0", fracPart = "00"] = abs.split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return `${negative ? "−" : ""}${currency} ${grouped}.${fracPart}`;
}
